import { useEffect, useRef, useState } from 'react';
import {
  useDispatch as useReduxDispatch,
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import State from '~Types/state';
import useI18nImport from '~Translations/';

import { useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';

export * from './Entities';
export * from './useBridgeApi';
export * from './useCashRegister';
export * from './useTextToImage';
export * from './useClickOutside';
export * from './usePrintBill';
export * from './usePromiseQuery';
export * from './useWindowSize';
export * from './useCurrency';
export * from './useDeviceInfo';
export * from './useGiftCards';
export * from './Integrations/useSKV';
export * from './Payments/usePaymentMethods';
export * from './useDates';
export * from './useOnboardingTracker';
export * from './useStatusOfDay';
export * from './ItemModifications/useItemModifications';
export * from './useBongHistory';
export * from './useMetadata';
export * from '../Providers/LicensesProvider';
export * from './useWaste';

export const useSelector: TypedUseSelectorHook<State> = useReduxSelector;

export const useI18n = useI18nImport;

export const useDispatch = () => useReduxDispatch<any>();

export function useInterval(callback: Function, delay: number | null) {
  const savedCallback = useRef<Function>();
  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current!();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }

    return () => {};
  }, [delay]);
}

export const useDebounce = (value: any = '', delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useIntegrationCheck = (integrationProvider: string) => {
  const activeIntegrations = useSelector((state) => state.activeIntegrations);
  return !!activeIntegrations.find(
    (integration) => integration.provider === integrationProvider && integration.isActive,
  );
};

export function usePrevious<T>(value: T): T {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current!;
}

export const useFragment = <T>(id: string, fragment: DocumentNode): T | null => {
  const client = useApolloClient();

  try {
    return client.readFragment({
      id,
      fragment,
    });
  } catch (e) {
    console.error(e);
  }

  return null;
};
